import { useState, FormEvent } from "react";
import OAuthButton from "./OAuthButton";
import { EyeIcon, EyeSlashIcon, UserPlusIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from "../../store/authStore";

interface SignUpViewProps {
  setView: (view: string) => void;
  onClose: () => void;
  invitation_code?:string;
}

const SignUpView = ({ setView , onClose,invitation_code}: SignUpViewProps) => {
  const [viewPwd, setViewPwd] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuthStore();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    setError("");
    const username = formData.get("username") as string;
    const password = formData.get("password") as string;
    const repeatPassword = formData.get("repeat_password") as string;
    if (!username) {
      setError("Email is required.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (password !== repeatPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!consent) {
      setError("Consent is required.");
      return;
    }
    setSubmitting(true);
    try {
      await register({ email: username, password , invitation_code}); 
      onClose();
    } catch (err: any) {
      setError(err.error || err.message || "Authentication error.");
    } finally {
      setSubmitting(false);
    }
  };

  const onOauthError = (error: string) => setError(error);

  if (![true,"true",1,"1"].includes(import.meta.env.VITE_ALLOW_SIGNUP) && !invitation_code) return <>Registration is by invitation only.</>

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="flex justify-center text-gray-500 mb-2">
        <UserPlusIcon className="w-8 h-8" />
      </div>
      <h1 className="text-2xl px-5 full-width text-center">Sign Up</h1>
      {error && <div className="py-1 px-4 my-2 text-xs bg-red-100 text-red-700 rounded">{error}</div>}
      <input
        placeholder="Enter your email"
        id="username"
        name="username"
        type="email"
        disabled={submitting}
        className="my-3 w-full p-2 border border-gray-300 rounded-md dark:bg-gray-900"
      />
      <div className="relative my-3">
        <input
          placeholder="Enter your password"
          id="password"
          name="password"
          type={viewPwd ? "text" : "password"}
          disabled={submitting}
          className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-900"
        />
        <button type="button" className="absolute right-2 top-2" onClick={() => setViewPwd(!viewPwd)} tabIndex={-1}>
          {viewPwd ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
        </button>
      </div>
      <div className="relative my-3">
        <input
          placeholder="Repeat your password"
          id="repeat_password"
          name="repeat_password"
          type={viewPwd ? "text" : "password"}
          disabled={submitting}
          className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-900"
        />
        <button type="button" className="absolute right-2 top-2" onClick={() => setViewPwd(!viewPwd)} tabIndex={-1}>
          {viewPwd ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
        </button>
      </div>
      <div className="pt-3 text-sm">
        <input
          type="checkbox"
          id="consent"
          name="consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={submitting}
          className="mr-2"
        />
        <label htmlFor="consent">
          I agree to the <a href="https://blust.ai/docs/terms" target="_blank" className="hover:underline">User Agreement</a> and <a href="https://blust.ai/docs/terms" target="_blank" className="hover:underline">Privacy Policy</a>.
        </label>
      </div>
      <button type="submit" disabled={submitting} className="w-full bg-blue-600 text-white rounded-md text-sm h-10 mt-5">
        Sign Up
      </button>
      <OAuthButton provider="google" disabled={submitting} consent={consent} onError={onOauthError} className="h-10 my-2" />
      <div className="flex justify-end pt-2 text-sm">
        <div className="text-gray-600 cursor-pointer hover:underline" onClick={() => setView("login")}>
          Already have an account?
        </div>
      </div>
    </form>
  );
};

export default SignUpView;
